/* eslint-disable unicorn/no-null -- Profile client uses `null` placeholders when profile metadata is missing. */

import { TRPCClientError } from '@trpc/client';
import { z } from 'zod';
import {
  getUserPreviewCacheEntry,
  invalidateUserPreviewCacheEntry,
  primeUserPreviewCacheFromProfile,
  resolveUserPreviewCacheKey,
  resolveUserPreviewCacheKeys,
  upsertUserPreviewCacheEntry,
} from '@/lib/cache/user-preview-cache';
import { createTrpcClient } from '@/lib/trpc/base';
import { isRecord } from '@/lib/utils/type-guards';
import type { ProfileResponse } from '@/lib/validation/api';
import { ProfileResponseSchema } from '@/lib/validation/api';
import type { UserPreviewVM } from '@/lib/view-models/user-preview';

const ProfileLookupSchema = z.object({
  id: z.string(),
  username: z.string(),
  name: z.string().nullable().optional(),
  profileImageUrl: z.string().nullable().optional(),
  isFollowing: z.boolean().optional(),
  followsYou: z.boolean().optional(),
});

export type ProfileLookupResult = z.infer<typeof ProfileLookupSchema>;

type LookupParameters = {
  profileId?: string | null;
  username?: string | null;
};

let cachedClient: ReturnType<typeof createTrpcClient> | null = null;

const getTrpcClient = () => {
  if (!cachedClient) {
    cachedClient = createTrpcClient();
  }
  return cachedClient;
};

type LookupProfileInput = { profileId?: string; username?: string };

const isLookupProfileQuery = (value: unknown): value is (input: LookupProfileInput) => Promise<unknown> =>
  typeof value === 'function';

const isRecordOrFunction = (value: unknown): value is Record<string, unknown> | (() => unknown) =>
  isRecord(value) || typeof value === 'function';

const getLookupProfileProcedure = () => {
  const client = getTrpcClient();
  if (!isRecordOrFunction(client)) {
    throw new Error('tRPC client is unavailable');
  }
  const profiles = client.profiles;
  if (!isRecordOrFunction(profiles)) {
    throw new Error('tRPC profiles segment is unavailable');
  }
  const lookupProcedure = profiles.lookupProfile;
  if (!isRecordOrFunction(lookupProcedure)) {
    throw new Error('tRPC lookupProfile procedure is unavailable');
  }
  const query = lookupProcedure.query;
  if (!isLookupProfileQuery(query)) {
    throw new Error('tRPC lookupProfile query is unavailable');
  }
  return {
    query,
  };
};

// Shared normalization helpers keep lookup/caller APIs aligned whether the source is the cache
// (user-preview) or a fresh ProfileResponse from the server. This gives us a single exit path that
// callers can trust, while letting us short-circuit duplicate fetches.
function buildLookupResultFromProfile(profile: ProfileResponse): ProfileLookupResult {
  return ProfileLookupSchema.parse({
    id: profile.id,
    username: profile.username,
    name: profile.name ?? undefined,
    profileImageUrl: profile.profileImageUrl ?? undefined,
    isFollowing: profile.isFollowing,
    followsYou: profile.followsYou,
  });
}

const validateCachedProfile = (profile: ProfileResponse | undefined, cacheKeys: string[]): ProfileResponse | null => {
  if (!profile) {
    return null;
  }
  const parsed = ProfileResponseSchema.safeParse(profile);
  if (parsed.success) {
    return parsed.data;
  }
  for (const key of cacheKeys) {
    invalidateUserPreviewCacheEntry(key);
  }
  return null;
};

function buildLookupResultFromPreview(preview: UserPreviewVM | undefined): ProfileLookupResult | undefined {
  if (!(preview?.id && preview.username)) {
    return;
  }
  return ProfileLookupSchema.parse({
    id: preview.id,
    username: preview.username,
    name: preview.name ?? undefined,
    profileImageUrl: preview.profileImageUrl ?? undefined,
    isFollowing: preview.isFollowing,
    followsYou: preview.followsYou,
  });
}

export async function lookupProfile(parameters: LookupParameters): Promise<ProfileLookupResult> {
  const cacheKeys = resolveUserPreviewCacheKeys({
    profileId: parameters.profileId ?? null,
    username: parameters.username ?? null,
  });

  for (const key of cacheKeys) {
    const cached = getUserPreviewCacheEntry(key);
    if (!cached) {
      continue;
    }
    if (cached.profile) {
      const validated = validateCachedProfile(cached.profile, cacheKeys);
      if (validated) {
        return buildLookupResultFromProfile(validated);
      }
    }
    const previewResult = buildLookupResultFromPreview(cached.preview);
    if (previewResult) {
      return previewResult;
    }
  }

  const input = (() => {
    const query: { profileId?: string; username?: string } = {};
    if (parameters.profileId) {
      query.profileId = parameters.profileId;
    }
    if (parameters.username) {
      query.username = parameters.username;
    }
    if (!(query.profileId || query.username)) {
      throw new Error('lookupProfile requires profileId or username');
    }
    return query;
  })();

  const response = await getLookupProfileProcedure().query(input);
  const parsed = ProfileResponseSchema.parse(response);
  // Prime every alias (id + username) so future hover/preview surfaces immediately reuse the data.
  primeUserPreviewCacheFromProfile(parsed);
  return buildLookupResultFromProfile(parsed);
}

export function lookupProfileByUsername(username: string): Promise<ProfileLookupResult> {
  return lookupProfile({ username });
}

function isNotFoundError(error: unknown): boolean {
  if (isRecord<Record<string, unknown> & { data?: unknown; response?: unknown }>(error)) {
    const errorData = error.data;
    if (
      isRecord<Record<string, unknown> & { httpStatus?: unknown }>(errorData) &&
      typeof errorData.httpStatus === 'number' &&
      errorData.httpStatus === 404
    ) {
      return true;
    }
    const response = error.response;
    return response instanceof Response && response.status === 404;
  }

  return error instanceof TRPCClientError && error.message.includes('404');
}

export async function fetchProfile(parameters: LookupParameters): Promise<ProfileResponse | null> {
  const { profileId, username } = parameters;

  if (profileId === undefined && !username) {
    return null;
  }

  const cacheKey = resolveUserPreviewCacheKey({ profileId: profileId ?? null, username: username ?? null }) ?? null;
  const cached = getUserPreviewCacheEntry(cacheKey);
  if (cached && cached.detail === 'full' && !cached.error && cached.profile) {
    // Reuse the cached payload; hover callers should not hit the network repeatedly.
    const cacheKeys = resolveUserPreviewCacheKeys({ profileId: profileId ?? null, username: username ?? null });
    const keysToInvalidate: string[] = [];
    if (cacheKeys.length > 0) {
      keysToInvalidate.push(...cacheKeys);
    } else if (cacheKey) {
      keysToInvalidate.push(cacheKey);
    }
    const validated = validateCachedProfile(cached.profile, keysToInvalidate);
    if (validated) {
      return validated;
    }
  }

  try {
    const response = await getLookupProfileProcedure().query({
      ...(profileId ? { profileId } : {}),
      ...(username ? { username } : {}),
    });
    const profile = ProfileResponseSchema.parse(response);
    // Successful lookups hydrate both cache aliases (id + username) so every future
    // hover around the app can reuse the same profile data.
    primeUserPreviewCacheFromProfile(profile);
    return profile;
  } catch (error) {
    if (isNotFoundError(error)) {
      const keys = resolveUserPreviewCacheKeys({
        profileId: profileId ?? null,
        username: username ?? null,
      });
      const targetKeys: string[] = [];
      if (keys.length > 0) {
        targetKeys.push(...keys);
      } else if (cacheKey) {
        targetKeys.push(cacheKey);
      }
      for (const key of targetKeys) {
        upsertUserPreviewCacheEntry(key, {
          preview: null,
          profile: null,
          error: true,
          detail: 'full',
        });
      }
      return null;
    }
    throw error;
  }
}

export function fetchProfileByUsername(username: string): Promise<ProfileResponse | null> {
  return fetchProfile({ username });
}

export async function fetchProfileById(profileId: string) {
  const profile = await fetchProfile({ profileId });
  if (!profile) {
    throw new Error('Profile not found');
  }
  return profile;
}
